import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Sun, CalendarRange, ListTodo, Plus } from "lucide-react";
import MeinTag from "@/pages/MeinTag";
import MeineWoche from "@/pages/MeineWoche";
import Tasks from "@/pages/Tasks";
import { TaskEditorProvider, useTaskEditor } from "@/context/TaskEditorContext";
import { useAuth } from "@/context/AuthContext";

const TAB_PATH = { heute: "/mein-tag", woche: "/meine-woche", aufgaben: "/tasks" };

function NewTaskButton({ tab }) {
  const { openCreate } = useTaskEditor();
  const { user } = useAuth();
  const handleClick = () => {
    // Aus dem Tages-/Wochenfokus heraus die Aufgabe direkt dem eigenen Nutzer zuweisen,
    // damit sie sofort im Wochen-Pool / Tagesvorschlag erscheint.
    const prefill = (tab === "heute" || tab === "woche") && user?.id
      ? { assigned_to_user_id: user.id }
      : {};
    openCreate(prefill);
  };
  return (
    <Button onClick={handleClick} data-testid="planung-new-task-btn" className="shrink-0">
      <Plus className="w-4 h-4 mr-2" />
      Neue Aufgabe
    </Button>
  );
}

export default function Planung({ initialTab = "aufgaben" }) {
  const navigate = useNavigate();
  const [tab, setTab] = useState(initialTab);

  const changeTab = (value) => {
    if (value === tab) return;
    setTab(value);
    const path = TAB_PATH[value] || "/tasks";
    navigate(path, { replace: true });
  };

  return (
    <TaskEditorProvider>
      <div className="space-y-6" data-testid="planung-page">
        <div className="brand-hero rounded-2xl p-5 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold tracking-tight">Aufgaben &amp; Planung</h1>
              <p className="text-muted-foreground mt-1">
                Tagesfokus, Wochenplanung und alle Aufgaben an einem Ort.
              </p>
            </div>
            <NewTaskButton tab={tab} />
          </div>
          <Tabs value={tab} onValueChange={changeTab} className="mt-4">
            <TabsList className="grid grid-cols-3 w-full max-w-lg">
              <TabsTrigger value="heute" data-testid="planung-tab-heute" className="gap-1.5">
                <Sun className="w-4 h-4" />
                Heute
              </TabsTrigger>
              <TabsTrigger value="woche" data-testid="planung-tab-woche" className="gap-1.5">
                <CalendarRange className="w-4 h-4" />
                Woche
              </TabsTrigger>
              <TabsTrigger value="aufgaben" data-testid="planung-tab-aufgaben" className="gap-1.5">
                <ListTodo className="w-4 h-4" />
                Alle Aufgaben
              </TabsTrigger>
            </TabsList>
          </Tabs>
        </div>

        <div className="animate-fade-in" key={tab}>
          {tab === "heute" && <MeinTag embedded />}
          {tab === "woche" && <MeineWoche embedded />}
          {tab === "aufgaben" && <Tasks embedded />}
        </div>
      </div>
    </TaskEditorProvider>
  );
}
